"use client";

import { motion } from "framer-motion";
import { useState, useRef, useEffect } from "react";

interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      name: string;
      alternativeText: string;
    };
  };
}

interface TabContent {
  id: number;
  heading: string;
  picture?: Picture;
}

interface Props {
  data: {
    title?: string;
    tabContent: TabContent[];
  };
}

export default function DataDisplayTabs_Layout1({ data }: Props) {
  const [active, setActive] = useState(1);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Minimum swipe distance (px) to trigger slide change
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwiping(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
    setIsSwiping(true);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe left - next slide
      setActive((prev) => (prev === data.tabContent.length - 1 ? 0 : prev + 1));
    } else if (isRightSwipe) {
      // Swipe right - previous slide
      setActive((prev) => (prev === 0 ? data.tabContent.length - 1 : prev - 1));
    }
    
    // Reset touch states
    setTouchStart(null);
    setTouchEnd(null);
    setIsSwiping(false);
  };

  // Prevent default touch actions on the container to avoid page scroll while swiping
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const preventDefault = (e: TouchEvent) => {
      if (isSwiping) {
        e.preventDefault();
      }
    };

    container.addEventListener('touchmove', preventDefault, { passive: false });
    
    return () => {
      container.removeEventListener('touchmove', preventDefault);
    };
  }, [isSwiping]);

  // Optional: Add keyboard navigation for accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setActive((prev) => (prev === 0 ? data.tabContent.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        setActive((prev) => (prev === data.tabContent.length - 1 ? 0 : prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [data.tabContent.length]);

  return (
    <section
      className="
        relative w-full overflow-hidden py-12 md:py-20
        bg-gradient-to-b from-[#fff5f7] via-white to-[#fdeef2]
      "
    >
      {/* TITLE */}
      <div className="text-center mb-8 md:mb-16 px-4">
        {data.title && (
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block text-2xl md:text-5xl font-light tracking-tight 
                       bg-gradient-to-r from-gray-700 to-black bg-clip-text text-transparent pb-1"
          >
            {data.title}

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
              className="h-[2px] bg-gray-800 w-full origin-left mt-2 md:mt-3"
            />
          </motion.h2>
        )}
      </div>

      {/* LEFT ARROW */}
      <button
        onClick={() =>
          setActive((prev) =>
            prev === 0 ? data.tabContent.length - 1 : prev - 1
          )
        }
        className="
          absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-20 bg-white shadow-lg w-10 h-10 
          rounded-full flex items-center justify-center text-lg hover:scale-105 transition-transform
        "
        aria-label="Previous slide"
      >
        ❮
      </button>

      {/* RIGHT ARROW */}
      <button
        onClick={() =>
          setActive((prev) =>
            prev === data.tabContent.length - 1 ? 0 : prev + 1
          )
        }
        className="
          absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-20 bg-white shadow-lg w-10 h-10 
          rounded-full flex items-center justify-center text-lg hover:scale-105 transition-transform
        "
        aria-label="Next slide"
      >
        ❯
      </button>

      {/* DESKTOP VIEW - original */}
      <div className="relative items-center justify-center gap-14 h-[520px] pointer-events-none hidden md:flex">
        {data.tabContent.map((item, index) => {
          const isActive = active === index;

          const baseStyle =
            "absolute transition-all duration-700 ease-[cubic-bezier(.4,0,.2,1)]";

          const positionStyles = isActive
            ? "z-30 scale-100 rotate-0 translate-x-0"
            : index < active
            ? "z-20 scale-90 -rotate-3 -translate-x-[230px]"
            : "z-20 scale-90 rotate-3 translate-x-[230px]";

          return (
            <motion.div
              key={item.id}
              className={`${baseStyle} ${positionStyles} w-[360px] h-[480px] rounded-2xl overflow-hidden shadow-xl`}
            >
              <img
                src={item.picture?.data?.attributes?.url}
                alt={item.heading}
                className="w-full h-full object-cover"
              />

              {/* HEADING */}
              <div className="absolute bottom-6 w-full text-center">
                <span className="text-white text-2xl font-light tracking-wide drop-shadow-2xl">
                  {item.heading}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* MOBILE VIEW - WITH TOUCH HANDLERS */}
      <div 
        ref={containerRef}
        className="md:hidden relative h-[380px] w-full overflow-visible flex items-center justify-center"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'pan-y' }} // Allow vertical scroll when not swiping horizontally
      >
        {data.tabContent.map((item, index) => {
          const isActive = active === index;

          const baseStyle =
            "absolute transition-all duration-500 ease-[cubic-bezier(.4,0,.2,1)]";

          // Same positioning logic as desktop, but with smaller offsets
          const positionStyles = isActive
            ? "z-30 scale-100 rotate-0 translate-x-0" // Center
            : index < active
            ? "z-20 scale-90 -rotate-3 -translate-x-[150px]" // Left
            : "z-20 scale-90 rotate-3 translate-x-[150px]"; // Right

          return (
            <motion.div
              key={item.id}
              className={`${baseStyle} ${positionStyles} w-[240px] h-[340px] rounded-xl overflow-hidden shadow-lg select-none`}

              style={{
                cursor: 'grab',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
              }}
            >
              <img
                src={item.picture?.data?.attributes?.url}
                alt={item.heading}
                className="w-full h-full object-cover pointer-events-none"
                draggable="false"
              />

              {/* HEADING */}
              <div className="absolute bottom-4 w-full text-center">
                <span className="text-white text-lg font-light tracking-wide drop-shadow-xl">
                  {item.heading}
                </span>
              </div>
            </motion.div>
          );
        })}
        
        {/* Optional: Swipe indicator (can be removed if not needed) */}
        {isSwiping && touchStart && touchEnd && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
            {touchStart > touchEnd ? "← Swiping left" : "→ Swiping right"}
          </div>
        )}
      </div>

      {/* DOTS INDICATOR */}
      <div className="flex justify-center gap-2 mt-8 md:hidden">
        {data.tabContent.map((_, index) => (
          <button
            key={index}
            onClick={() => setActive(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === active ? "bg-gray-800 scale-125" : "bg-gray-300"
            }`}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === active ? "true" : "false"}
          />
        ))}
      </div>

      {/* Optional: Swipe instructions for mobile users */}
      {/* <div className="text-center mt-4 md:hidden text-gray-500 text-sm">
        <p>Swipe left or right to navigate</p>
      </div> */}
    </section>
  );
}